import type { Lang } from '../types';

export interface CalendarManagerText {
  title: string;
  description: string;
  loadingCalendars: string;
  noCalendars: string;
  syncedAt: string;
  copyUrl: string;
  syncNow: string;
  delete: string;
  addCalendar: string;
  priorityUp: string;
  priorityDown: string;
  formNameLabel: string;
  formSourceLabel: string;
  formUrlLabel: string;
  formUrlHint: string;
  adding: string;
  cancel: string;
  confirmDelete: string;
  successAdded: string;
  successDeleted: string;
  errorDuplicate: string;
  syncInfoNote: string;
  syncComplete: string;
  syncNoNew: string;
  syncNewCount: (n: number) => string;
  syncUpdatedCount: (n: number) => string;
  syncCancelledCount: (n: number) => string;
  syncSkippedPastCount: (n: number) => string;
  infoPlatformsLabel: string;
  infoIcalSub: string;
  infoGuestbookLabel: string;
  infoGuestbookSub: string;
  infoImportedLabel: string;
  infoImportedSub: string;
  infoOtherPlatforms: string;
  infoPriorityTitle: string;
  infoPriorityDesc: string;
  infoGuideLink: string;
  infoGuideUrl: string;
}

export const calendarManagerText: Record<Lang, CalendarManagerText> = {
  cs: {
    title: 'Propojené kalendáře (import iCal)',
    description: 'Importujte rezervace z externích kalendářů (Airbnb, Booking.com atd.) a automaticky blokujte termíny.',
    loadingCalendars: 'Načítám kalendáře…',
    noCalendars: 'Zatím žádné kalendáře.',
    syncedAt: 'Synchronizováno:',
    copyUrl: 'Kopírovat',
    syncNow: 'Synchronizovat',
    delete: 'Smazat',
    addCalendar: 'Přidat kalendář',
    priorityUp: 'Vyšší priorita',
    priorityDown: 'Nižší priorita',
    formNameLabel: 'Název kalendáře (např. „Airbnb kalendář"):',
    formSourceLabel: 'Zdroj:',
    formUrlLabel: 'iCal Feed URL:',
    formUrlHint: 'Tuto adresu najdete v nastavení exportu kalendáře na vaší rezervační platformě.',
    adding: 'Přidávám…',
    cancel: 'Zrušit',
    confirmDelete: 'Smazat tento kalendář? Tím se odstraní i všechny importované rezervace z tohoto zdroje.',
    successAdded: 'Kalendář přidán',
    successDeleted: 'Kalendář odstraněn',
    errorDuplicate: 'Tento iCal URL je již propojen s tímto bytem.',
    syncInfoNote: 'Importují se pouze aktuální a budoucí rezervace. Minulé rezervace (host již odjel) jsou ignorovány.',
    syncComplete: 'Synchronizace dokončena',
    syncNoNew: 'žádné nové rezervace',
    syncNewCount: (n) =>
      `${n} ${n === 1 ? 'nová rezervace importována' : n >= 2 && n <= 4 ? 'nové rezervace importovány' : 'nových rezervací importováno'}`,
    syncUpdatedCount: (n) => `${n} aktualizováno`,
    syncCancelledCount: (n) => `${n} zrušeno`,
    syncSkippedPastCount: (n) =>
      `${n} ${n === 1 ? 'minulá rezervace ignorována' : n >= 2 && n <= 4 ? 'minulé rezervace ignorovány' : 'minulých rezervací ignorováno'}`,
    infoPlatformsLabel: 'Vaše platformy',
    infoIcalSub: 'Zkopírujte odkaz na export kalendáře',
    infoGuestbookLabel: 'Guestbook importuje',
    infoGuestbookSub: 'Automaticky stahuje nové rezervace',
    infoImportedLabel: 'Rezervace importovány',
    infoImportedSub: 'Vše připraveno v kalendáři',
    infoOtherPlatforms: 'jiné',
    infoPriorityTitle: 'Priorita & přebukování:',
    infoPriorityDesc: 'Při překryvu dvou kalendářů vyhraje ten s vyšší prioritou — vrchní kalendář vždy vítězí. Pořadí změníte tlačítky ▲▼ níže. Manuální rezervace mají vždy přednost.',
    infoGuideLink: 'Jak nastavit iCal sync →',
    infoGuideUrl: 'https://guestbook.cz/guide.html#krok-2',
  },
  en: {
    title: 'Linked Calendars (iCal Import)',
    description: 'Import reservations from external calendars (Airbnb, Booking.com, etc.) to block dates automatically.',
    loadingCalendars: 'Loading calendars…',
    noCalendars: 'No calendars linked yet.',
    syncedAt: 'Synced:',
    copyUrl: 'Copy',
    syncNow: 'Sync Now',
    delete: 'Delete',
    addCalendar: 'Add Calendar',
    priorityUp: 'Higher priority',
    priorityDown: 'Lower priority',
    formNameLabel: 'Calendar Name (e.g., "Airbnb Calendar"):',
    formSourceLabel: 'Source:',
    formUrlLabel: 'iCal Feed URL:',
    formUrlHint: "Get this URL from your booking platform's calendar export settings.",
    adding: 'Adding…',
    cancel: 'Cancel',
    confirmDelete: 'Delete this calendar? This will also remove all imported reservations from this source.',
    successAdded: 'Calendar added successfully',
    successDeleted: 'Calendar deleted',
    errorDuplicate: 'This calendar URL is already linked to this apartment.',
    syncInfoNote: 'Only current and upcoming reservations are imported. Past reservations (already checked out) are ignored.',
    syncComplete: 'Sync complete',
    syncNoNew: 'no new reservations',
    syncNewCount: (n) => `${n} new reservation${n !== 1 ? 's' : ''} imported`,
    syncUpdatedCount: (n) => `${n} updated`,
    syncCancelledCount: (n) => `${n} cancelled`,
    syncSkippedPastCount: (n) => `${n} past reservation${n !== 1 ? 's' : ''} ignored`,
    infoPlatformsLabel: 'Your platforms',
    infoIcalSub: 'Copy the calendar export link',
    infoGuestbookLabel: 'Guestbook imports',
    infoGuestbookSub: 'Automatically fetches new reservations',
    infoImportedLabel: 'Reservations imported',
    infoImportedSub: 'Ready in your calendar',
    infoOtherPlatforms: 'other',
    infoPriorityTitle: 'Priority & overbooking:',
    infoPriorityDesc: 'When two calendars overlap, the higher-priority one wins — the top calendar always wins. Reorder using ▲▼ below. Manual reservations always take priority.',
    infoGuideLink: 'How to set up iCal sync →',
    infoGuideUrl: 'https://guestbook.cz/en/guide.html#step-2',
  },
  vi: {
    title: 'Liên kết lịch (nhập iCal)',
    description: 'Nhập đặt phòng từ lịch bên ngoài (Airbnb, Booking.com, v.v.) để tự động chặn ngày.',
    loadingCalendars: 'Đang tải lịch…',
    noCalendars: 'Chưa có lịch nào được liên kết.',
    syncedAt: 'Đồng bộ lúc:',
    copyUrl: 'Sao chép',
    syncNow: 'Đồng bộ ngay',
    delete: 'Xóa',
    addCalendar: 'Thêm lịch',
    priorityUp: 'Ưu tiên cao hơn',
    priorityDown: 'Ưu tiên thấp hơn',
    formNameLabel: 'Tên lịch (vd. "Lịch Airbnb"):',
    formSourceLabel: 'Nguồn:',
    formUrlLabel: 'URL iCal Feed:',
    formUrlHint: 'Lấy URL này từ cài đặt xuất lịch của nền tảng đặt phòng của bạn.',
    adding: 'Đang thêm…',
    cancel: 'Hủy',
    confirmDelete: 'Xóa lịch này? Thao tác này cũng sẽ xóa tất cả các đặt phòng đã nhập từ nguồn này.',
    successAdded: 'Đã thêm lịch thành công',
    successDeleted: 'Đã xóa lịch',
    errorDuplicate: 'URL lịch này đã được liên kết với căn hộ này.',
    syncInfoNote: 'Chỉ nhập các đặt phòng hiện tại và sắp tới. Các đặt phòng trong quá khứ (đã trả phòng) sẽ bị bỏ qua.',
    syncComplete: 'Đồng bộ hoàn tất',
    syncNoNew: 'không có đặt phòng mới',
    syncNewCount: (n) => `đã nhập ${n} đặt phòng mới`,
    syncUpdatedCount: (n) => `${n} đã cập nhật`,
    syncCancelledCount: (n) => `${n} đã hủy`,
    syncSkippedPastCount: (n) => `bỏ qua ${n} đặt phòng trong quá khứ`,
    infoPlatformsLabel: 'Nền tảng của bạn',
    infoIcalSub: 'Sao chép liên kết xuất lịch',
    infoGuestbookLabel: 'Guestbook nhập',
    infoGuestbookSub: 'Tự động tải đặt phòng mới',
    infoImportedLabel: 'Đặt phòng đã nhập',
    infoImportedSub: 'Sẵn sàng trong lịch',
    infoOtherPlatforms: 'khác',
    infoPriorityTitle: 'Ưu tiên & đặt quá mức:',
    infoPriorityDesc: 'Khi hai lịch trùng lặp, lịch có độ ưu tiên cao hơn sẽ thắng — lịch trên cùng luôn thắng. Sắp xếp lại bằng ▲▼ bên dưới. Đặt phòng thủ công luôn được ưu tiên.',
    infoGuideLink: 'Cách thiết lập đồng bộ iCal →',
    infoGuideUrl: 'https://guestbook.cz/vi/guide.html#step-2',
  },
  uk: {
    title: "Прив'язані календарі (імпорт iCal)",
    description: "Імпортуйте бронювання з зовнішніх календарів (Airbnb, Booking.com тощо) для автоматичного блокування дат.",
    loadingCalendars: 'Завантаження календарів…',
    noCalendars: "Жодного календаря не прив'язано.",
    syncedAt: 'Синхронізовано:',
    copyUrl: 'Копіювати',
    syncNow: 'Синхронізувати',
    delete: 'Видалити',
    addCalendar: 'Додати календар',
    priorityUp: 'Вищий пріоритет',
    priorityDown: 'Нижчий пріоритет',
    formNameLabel: 'Назва календаря (напр. «Airbnb календар»):',
    formSourceLabel: 'Джерело:',
    formUrlLabel: 'URL iCal-фіду:',
    formUrlHint: 'Отримайте цю адресу в налаштуваннях експорту календаря на вашій платформі бронювання.',
    adding: 'Додаємо…',
    cancel: 'Скасувати',
    confirmDelete: 'Видалити цей календар? Це також видалить усі імпортовані бронювання з цього джерела.',
    successAdded: 'Календар успішно додано',
    successDeleted: 'Календар видалено',
    errorDuplicate: "Цей iCal URL вже прив'язаний до цього апартаменту.",
    syncInfoNote: 'Імпортуються лише поточні та майбутні бронювання. Минулі бронювання (гість вже виїхав) ігноруються.',
    syncComplete: 'Синхронізацію завершено',
    syncNoNew: 'нових бронювань немає',
    syncNewCount: (n) =>
      `${n} ${n === 1 ? 'нове бронювання імпортовано' : n >= 2 && n <= 4 ? 'нових бронювання імпортовано' : 'нових бронювань імпортовано'}`,
    syncUpdatedCount: (n) => `${n} оновлено`,
    syncCancelledCount: (n) => `${n} скасовано`,
    syncSkippedPastCount: (n) =>
      `${n} ${n === 1 ? 'минуле бронювання проігноровано' : n >= 2 && n <= 4 ? 'минулих бронювання проігноровано' : 'минулих бронювань проігноровано'}`,
    infoPlatformsLabel: 'Ваші платформи',
    infoIcalSub: 'Скопіюйте посилання на експорт',
    infoGuestbookLabel: 'Guestbook імпортує',
    infoGuestbookSub: 'Автоматично завантажує нові бронювання',
    infoImportedLabel: 'Бронювання імпортовано',
    infoImportedSub: 'Все готово в календарі',
    infoOtherPlatforms: 'інше',
    infoPriorityTitle: 'Пріоритет & перебронювання:',
    infoPriorityDesc: 'При перетині двох календарів перемагає той з вищим пріоритетом — верхній завжди перемагає. Змініть порядок кнопками ▲▼ нижче. Ручні бронювання завжди мають пріоритет.',
    infoGuideLink: 'Як налаштувати iCal синхронізацію →',
    infoGuideUrl: 'https://guestbook.cz/uk/guide.html#step-2',
  },
};
